import { Inject, Injectable } from '@angular/core';
import { CONFIG, EnvironmentConfig } from '@kenv';
import * as PurchasesModule from '@revenuecat/purchases-js';
import { DataStoreService } from './data-store.service';
import { WINDOW } from './window.service';

type PurchasesApi = {
  configure?: (args: { apiKey: string; appUserId?: string }) => Promise<unknown> | unknown;
  getCustomerInfo?: () => Promise<unknown> | unknown;
  getOfferings?: () => Promise<unknown> | unknown;
  presentPaywall?: (args: {
    htmlTarget: HTMLElement;
    offering?: unknown;
    offeringId?: string;
    offeringIdentifier?: string;
    customerEmail?: string;
  }) => Promise<unknown> | unknown;
  setAttributes?: (attributes: Record<string, string>) => Promise<unknown> | unknown;
  setEmail?: (email: string) => Promise<unknown> | unknown;
  // Identity methods have changed names across SDK versions.
  logIn?: (userId: string) => Promise<unknown> | unknown;
  changeUser?: (userId: string) => Promise<unknown> | unknown;
  setUserId?: (userId: string) => Promise<unknown> | unknown;
};

type PurchasesCandidate = PurchasesApi & { instance?: PurchasesApi | null };

function hasFn<T extends object, K extends keyof T>(
  obj: T | null | undefined,
  key: K
): obj is T & Required<Pick<T, K>> {
  const value = (obj as Record<string, unknown> | null | undefined)?.[key as string];
  return typeof value === 'function';
}

/**
 * RevenueCat Web Billing integration wrapper.
 *
 * Notes:
 * - Uses the Web Billing public API key (sandbox keys usually start with `rcb_sb_`).
 * - Best-effort: we keep the SDK configured and (re)identify with the current Kumanu user id.
 * - We intentionally use `any` for some calls because the purchases-js API surface has changed
 *   over time (logIn vs changeUser, etc.). This keeps the app resilient across minor SDK updates.
 */
@Injectable({
  providedIn: 'root',
})
export class RevenueCatWebBillingService {
  private _configured = false;
  private _lastConfiguredUserId: string | null = null;
  private _purchasesInstance: PurchasesApi | null = null;

  constructor(
    @Inject(CONFIG) private _env: EnvironmentConfig,
    private _dataStore: DataStoreService,
    @Inject(WINDOW) private _window: Window
  ) {}

  private get _apiKey(): string {
    return this._env?.revenueCatWebPublicApiKey || '';
  }

  private get _userId(): string | null {
    return this._dataStore?.authData?.user?.id || null;
  }

  private get _userEmail(): string | null {
    return this._dataStore?.authData?.user?.email || null;
  }

  private _isBrowser(): boolean {
    // WINDOW is {} during SSR / tests; avoid calling Purchases in those contexts.
    return typeof this._window?.location?.origin === 'string';
  }

  private get _purchases(): PurchasesApi {
    // Purchases-js export can vary (default vs named); normalize here.
    const module = PurchasesModule as unknown as {
      default?: PurchasesApi;
      Purchases?: PurchasesApi;
    };
    const candidate = (module.default || module.Purchases || module) as PurchasesApi;
    return this._purchasesInstance || candidate;
  }

  private _debugPurchasesKeys(purchases: PurchasesApi): string {
    try {
      const keys = Object.keys(purchases || {});
      return keys.length ? keys.join(', ') : 'none';
    } catch {
      return 'unknown';
    }
  }

  private _getPurchasesModuleCandidates(): PurchasesCandidate[] {
    const module = PurchasesModule as unknown as {
      default?: PurchasesCandidate;
      Purchases?: PurchasesCandidate;
      instance?: PurchasesApi;
    };
    const candidates: PurchasesCandidate[] = [];
    if (this._purchasesInstance) candidates.push(this._purchasesInstance);
    if (module.default) candidates.push(module.default);
    if (module.default?.instance) candidates.push(module.default.instance);
    if (module.Purchases) candidates.push(module.Purchases);
    if (module.Purchases?.instance) candidates.push(module.Purchases.instance);
    if (module.instance) candidates.push(module.instance);
    candidates.push(module as unknown as PurchasesApi);
    return candidates.filter(Boolean);
  }

  private _resolvePaywallPurchases(): PurchasesApi | null {
    const candidates = this._getPurchasesModuleCandidates();
    for (const candidate of candidates) {
      if (hasFn(candidate, 'presentPaywall')) return candidate;
      const instance = candidate.instance ?? undefined;
      if (instance && hasFn(instance, 'presentPaywall')) return instance;
    }
    return null;
  }

  async configureIfNeeded(): Promise<boolean> {
    if (!this._isBrowser()) return false;
    if (this._configured) {
      // Even if configured, try to keep identity aligned.
      await this._syncIdentityBestEffort();
      return true;
    }

    const apiKey = this._apiKey;
    if (!apiKey) return false;

    try {
      const userId = this._userId || undefined;
      const purchases = this._purchases as PurchasesApi & { instance?: PurchasesApi };
      if (!hasFn(purchases, 'configure')) {
        return false;
      }
      const configured = await purchases.configure({ apiKey, appUserId: userId });
      if (configured && typeof configured === 'object') {
        this._purchasesInstance = configured as PurchasesApi;
      } else if (purchases.instance && typeof purchases.instance === 'object') {
        this._purchasesInstance = purchases.instance;
      }
      this._configured = true;
      this._lastConfiguredUserId = this._userId;
      await this._syncIdentityBestEffort();
      return true;
    } catch (e) {
      // Keep this non-fatal; the paywall screen will show a friendly error.
      this._configured = false;
      return false;
    }
  }

  private async _syncIdentityBestEffort(): Promise<void> {
    if (!this._configured) return;
    const userId = this._userId;
    if (!userId) return;
    if (this._lastConfiguredUserId === userId) return;

    try {
      const purchases = this._purchases;
      if (hasFn(purchases, 'logIn')) {
        await purchases.logIn(userId);
      } else if (hasFn(purchases, 'changeUser')) {
        await purchases.changeUser(userId);
      } else if (hasFn(purchases, 'setUserId')) {
        await purchases.setUserId(userId);
      }
      this._lastConfiguredUserId = userId;
    } catch {
      // Best-effort; ignore.
    }
  }

  async getCustomerInfo(): Promise<unknown | null> {
    const ok = await this.configureIfNeeded();
    if (!ok) return null;
    try {
      const purchases = this._purchases;
      if (!hasFn(purchases, 'getCustomerInfo')) return null;
      return await purchases.getCustomerInfo();
    } catch {
      return null;
    }
  }

  /**
   * Checks `premium` entitlement (matches your mobile entitlement id).
   */
  hasPremiumEntitlement(customerInfo: unknown | null): boolean {
    const info = customerInfo as { entitlements?: { active?: { premium?: unknown } } } | null;
    return Boolean(info?.entitlements?.active?.premium);
  }

  /**
   * Presents (renders) the RevenueCat Paywall into the provided element.
   *
   * The returned value depends on SDK version; callers should treat it as opaque.
   */
  async presentPaywall(params: { htmlTarget: HTMLElement; offeringId?: string | null }): Promise<unknown> {
    const ok = await this.configureIfNeeded();
    if (!ok) throw new Error('RevenueCat is not configured');

    // Fail closed on web: if we were given a specific offering id but cannot
    // resolve it from current RevenueCat offerings, do not fall back to default.
    const requestedOfferingId = (params.offeringId || '').trim();
    let resolvedOffering: unknown | null = null;
    if (requestedOfferingId) {
      resolvedOffering = await this._resolveOfferingById(requestedOfferingId);
      if (!resolvedOffering) {
        throw new Error(`Requested offering "${requestedOfferingId}" is not available for this web billing context`);
      }
    }

    const purchases = this._resolvePaywallPurchases();
    if (!hasFn(purchases, 'presentPaywall')) {
      const candidates = this._getPurchasesModuleCandidates();
      const keys = candidates.map((cand) => this._debugPurchasesKeys(cand)).join(' | ');
      throw new Error(`RevenueCat Paywalls are not available. Purchases keys: ${keys}`);
    }

    // purchases-js v1.x expects `offering` object for targeted paywalls.
    // We still pass legacy keys as no-op compatibility for older variants.
    return await purchases.presentPaywall({
      htmlTarget: params.htmlTarget,
      offering: resolvedOffering || undefined,
      offeringId: params.offeringId || undefined,
      offeringIdentifier: params.offeringId || undefined,
      customerEmail: this._userEmail || undefined,
    });
  }

  private async _resolveOfferingById(offeringId: string): Promise<unknown | null> {
    const candidates = this._getPurchasesModuleCandidates();
    const seen = new Set<unknown>();

    for (const candidate of candidates) {
      if (seen.has(candidate)) continue;
      seen.add(candidate);

      const instance = candidate.instance ?? candidate;
      if (!hasFn(instance, 'getOfferings')) {
        continue;
      }

      try {
        const offerings = await instance.getOfferings();
        const offering = this._findOfferingById(offerings, offeringId);
        if (offering) {
          return offering;
        }
        return null;
      } catch {
        // Continue trying other candidates before concluding unavailable.
      }
    }

    // Could not verify offerings from the SDK; fail closed to avoid default fallback.
    return null;
  }

  private _findOfferingById(offerings: unknown, offeringId: string): unknown | null {
    if (!offerings || typeof offerings !== 'object') return false;
    const obj = offerings as Record<string, unknown>;
    const normalizedTarget = offeringId.trim();
    if (!normalizedTarget) return null;

    const all = obj['all'];
    if (all && typeof all === 'object' && !Array.isArray(all)) {
      const match = (all as Record<string, unknown>)[normalizedTarget];
      if (match != null) return match;
    }

    const list = obj['offerings'];
    if (Array.isArray(list)) {
      for (const entry of list as unknown[]) {
        if (!entry || typeof entry !== 'object') continue;
        const identifier = (entry as Record<string, unknown>)['identifier'];
        if (typeof identifier === 'string' && identifier.trim() === normalizedTarget) {
          return entry;
        }
      }
    }

    const current = obj['current'];
    if (current && typeof current === 'object') {
      const identifier = (current as Record<string, unknown>)['identifier'];
      if (typeof identifier === 'string' && identifier.trim() === normalizedTarget) {
        return current;
      }
    }

    return null;
  }

  // Customer Center intentionally omitted; we rely on management URLs instead.
}
